// Cross-document backlink extraction.
//
// Docs are markdown files in a git repo. Their cross-doc identity is the
// "family" slug — the directory segment that holds a family's versions, e.g.
//   methodologies/blue-index-methodology/v0.1.md  -> family "blue-index-methodology"
//
// Documents reference each other in a few plausible ways:
//   - markdown link targets that contain another family's path, e.g.
//       [BLUE](methodologies/blue-index-methodology/v0.1.md)
//       [BLUE](../blue-index-methodology/v0.2.md)
//   - wiki-style refs:  [[blue-index-methodology]]
//
// We are deliberately conservative: we match link targets and wiki refs, plus
// bare occurrences only when the slug is bounded like a path/ref token (e.g.
// `blue-index-methodology/`). We avoid matching plain prose to keep false
// positives down — a family slug appearing in a sentence is not a reference.

/** Markdown link / image target:  [text](target)  or  ![alt](target) */
const MD_LINK_RE = /!?\[[^\]]*\]\(\s*([^)\s]+)/g;

/** Wiki-style ref:  [[slug]] or [[slug|label]] or [[slug/v0.1]] */
const WIKI_REF_RE = /\[\[\s*([^\]|]+?)\s*(?:\|[^\]]*)?\]\]/g;

/** Reference-style link definition:  [id]: target */
const MD_REF_DEF_RE = /^\s*\[[^\]]+\]:\s*(\S+)/gm;

/** Pull the first path segment that matches a known slug out of a link target. */
function slugFromTarget(target: string, known: Set<string>): string | null {
  // Strip any anchor / query, then split on path separators.
  const clean = target.split(/[?#]/)[0];
  for (const seg of clean.split("/")) {
    const s = seg.trim();
    if (s && known.has(s)) return s;
  }
  return null;
}

/**
 * Extract the set of known family slugs referenced by a markdown body.
 *
 * @param body   raw markdown (front matter may be present; we scan the whole
 *               string — front-matter values rarely collide with link syntax)
 * @param known  the set of all family slugs that exist in the repo
 * @param self   optional slug of the document being scanned, excluded from
 *               results so a doc never lists itself
 */
export function extractReferencedSlugs(
  body: string,
  known: Set<string>,
  self?: string,
): string[] {
  const found = new Set<string>();

  const consider = (slug: string | null) => {
    if (slug && slug !== self && known.has(slug)) found.add(slug);
  };

  // 1. Markdown inline links / images.
  for (const m of body.matchAll(MD_LINK_RE)) {
    consider(slugFromTarget(m[1], known));
  }

  // 2. Reference-style link definitions.
  for (const m of body.matchAll(MD_REF_DEF_RE)) {
    consider(slugFromTarget(m[1], known));
  }

  // 3. Wiki-style refs — the inner value may itself be a path (slug/v0.1).
  for (const m of body.matchAll(WIKI_REF_RE)) {
    const inner = m[1].trim();
    // Direct slug match, or a slug as the leading path segment.
    if (known.has(inner)) consider(inner);
    else consider(slugFromTarget(inner, known));
  }

  // 4. Bare path-like occurrences: `<slug>/` anywhere (covers raw paths in
  //    prose/tables that aren't wrapped in link syntax). Bounded by a
  //    trailing slash so we only match path/ref usages, never plain prose.
  for (const slug of known) {
    if (slug === self) continue;
    if (found.has(slug)) continue;
    // Require a non-word char (or start) before the slug and a "/" after, so
    // "blue-index-methodology/" matches but "the blue index" does not, and a
    // longer slug ending in this one doesn't spuriously match.
    const re = new RegExp(`(^|[^\\w/-])${escapeRegExp(slug)}/`);
    if (re.test(body)) found.add(slug);
  }

  return [...found];
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** A document body paired with the family it belongs to. */
export interface DocSource {
  family: string;
  body: string;
}

/**
 * Build a reverse index: family slug -> sorted list of OTHER families that
 * reference it. Only families present in `sources` (i.e. with a known slug)
 * appear as referrers; targets are constrained to the same known set.
 */
export function buildBacklinkIndex(
  sources: DocSource[],
): Map<string, string[]> {
  const known = new Set(sources.map((s) => s.family));
  const index = new Map<string, Set<string>>();

  for (const { family, body } of sources) {
    for (const target of extractReferencedSlugs(body, known, family)) {
      let set = index.get(target);
      if (!set) {
        set = new Set<string>();
        index.set(target, set);
      }
      set.add(family);
    }
  }

  const out = new Map<string, string[]>();
  for (const [target, referrers] of index) {
    out.set(target, [...referrers].sort());
  }
  return out;
}

/** Convenience: inbound referrers for a single family from a built index. */
export function backlinksFor(
  index: Map<string, string[]>,
  family: string,
): string[] {
  return index.get(family) ?? [];
}
